import { useCreateBulkApprovingInvoiceReportApprovalMutation } from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { useApprovingInvoiceReportsSearchRefetch } from '@/context/services/approving_invoice_reports/refetchContext';
import { useConfirmApproveModal } from '@/features/approving_invoice_reports/components/ConfirmApproveModal/context/ConfirmApproveModalContext';
import { useCheckApprovingReportsProtectedResources } from '@/features/approving_invoice_reports/components/hooks/useCheckApprovingReportsProtectedResources';
import { useTriggerFindTargetReport } from '@/features/approving_invoice_reports/components/List/context/TriggerFindTargetReportContext';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import parser from 'html-react-parser';
import { useCallback } from 'react';

type UseBulkApproveReportsProps = {
  onSuccess?: () => void;
};

export type ApprovingInvoiceReport = {
  report_id: string;
  report_approval_id: string;
  number: string;
};

type ResultItem = {
  report_id: string;
  report_approval_id: string;
  number: string;
  status: 'success' | 'failed';
};

const NOTIFICATION_DURATION = 3 as const;

const statusCodesCheckAccessPermission = ['401', '403', '404'];

const calculateResults = (
  requestedData: ApprovingInvoiceReport[],
  results: ResultItem[]
): {
  succeedCount: number;
  failedCount: number;
  failedItemNumbers: string[];
} => {
  const succeedItemIds = results
    .filter((item) => item.status === 'success')
    .map((item) => item.report_id);

  const failedItemNumbers = requestedData
    .filter((item) => !succeedItemIds.includes(item.report_id))
    .map((item) => item.number);

  return {
    failedCount: failedItemNumbers.length,
    succeedCount: succeedItemIds.length,
    failedItemNumbers,
  };
};

export const useBulkApproveReports = ({
  onSuccess,
}: UseBulkApproveReportsProps = {}) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { mutateAsync } = useCreateBulkApprovingInvoiceReportApprovalMutation();
  const refetch = useApprovingInvoiceReportsSearchRefetch();
  const { onTriggerBackToPageOneIfNoData } = useTriggerFindTargetReport();

  const { handleCheckPermission } =
    useCheckApprovingReportsProtectedResources();
  const { onCloseConfirmApproveModal } = useConfirmApproveModal();
  const onCheckAccessApprovingReportSuccess = useCallback(
    (message?: string) => () => {
      onCloseConfirmApproveModal();
      onTriggerBackToPageOneIfNoData();
      notify?.error({
        title:
          message || t('bulk_approve_invoice_reports_unexpected_error_message'),
        duration: NOTIFICATION_DURATION,
      });
    },
    [notify, t, onCloseConfirmApproveModal, onTriggerBackToPageOneIfNoData]
  );

  const bulkApproveReports = useCallback(
    (selectedReports: ApprovingInvoiceReport[]) => async () => {
      if (!selectedReports?.length) {
        notify?.error({
          title: t('no_invoice_reports_selected'),
          duration: NOTIFICATION_DURATION,
        });
        return;
      }

      const reports = selectedReports.map((report) => ({
        report_id: report.report_id,
        report_approval_id: report.report_approval_id,
      }));
      const resp = await mutateAsync({ reports });

      if (resp.status === 'success') {
        const { succeedCount, failedCount, failedItemNumbers } =
          calculateResults(selectedReports, resp?.data?.result ?? []);
        await refetch?.();
        onSuccess?.();

        if (succeedCount > 0) {
          notify?.success({
            title: t('bulk_approve_invoice_reports_success_items_message', {
              count: succeedCount,
            }),
            duration: NOTIFICATION_DURATION,
          });

          if (failedCount > 0) {
            notify?.error({
              title: parser(
                t('bulk_approve_invoice_reports_failed_items_message', {
                  report_numbers: failedItemNumbers.join('、'),
                })
              ),
              duration: NOTIFICATION_DURATION,
            });
          }
        } else {
          notify?.error({
            title: t('bulk_approve_invoice_reports_unexpected_error_message'),
            duration: NOTIFICATION_DURATION,
          });
        }
        onTriggerBackToPageOneIfNoData();
      } else {
        if (
          statusCodesCheckAccessPermission.includes(resp.errors?.code || '')
        ) {
          handleCheckPermission({
            fnCheckPermission: refetch,
            onCheckSuccess: onCheckAccessApprovingReportSuccess(
              resp?.errors?.messages?.join(' ')
            ),
            onCheckFailure: () => {},
          });
          return;
        }
        notify?.error({
          title:
            resp?.errors?.messages?.join(', ') ||
            t('bulk_approve_invoice_reports_unexpected_error_message'),
          duration: NOTIFICATION_DURATION,
        });
      }
      onCloseConfirmApproveModal();
    },
    [
      mutateAsync,
      notify,
      refetch,
      t,
      onSuccess,
      handleCheckPermission,
      onCheckAccessApprovingReportSuccess,
      onCloseConfirmApproveModal,
      onTriggerBackToPageOneIfNoData,
    ]
  );

  return {
    bulkApproveReports,
  };
};
